import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getVgsapPromotionDetail } from "../../service/SalesNewsVgsap";
import { PageLoader } from "../../component/loader/PLoader";
import ErrorHandler from "../../component/errors/ErrorHandler";
import {
	Box, Typography, Paper, Breadcrumbs, Chip, Divider, Button
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import useUser from "../../hooks/useUser";
import "./salesNewsVgsap.css";
import { formatDateOrNA as formatDate, DetailField, POINT_SECTIONS } from "./vgsapUtils";

function PromotionDetail() {
	const { promoId } = useParams();
	const { t } = useTranslation();
	const { isSalesNewsAdmin } = useUser();

	// Fetch promotion detail + points
	const { data: promo, isLoading, error } = useQuery({
		queryKey: ["vgsapPromotionDetail", promoId],
		queryFn: () => getVgsapPromotionDetail(promoId),
	});

	if (isLoading) return <PageLoader show={true} />;
	if (error) return <ErrorHandler title={t("Something went wrong")} desc={t("Unable to load promotion details")} />;
	if (!promo) return <ErrorHandler title={t("Not Found")} desc={t("Promotion not found")} />;

	// Group points by promo_bullet_id
	const pointsBySection = (promo.points || []).reduce((acc, point) => {
		const section = point.promo_bullet_id || point.Promo_bullet_id || '1';
		if (!acc[section]) acc[section] = [];
		acc[section].push(point);
		return acc;
	}, {});

	return (
		<Box sx={{ p: 3 }} className="vgsap__page">
			{/* Breadcrumb */}
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
				<Link to="/sales-news/sales-news-vgsap" style={{ textDecoration: "none", color: "#1976d2" }}>
					{t("Sales News (Vistex GSAP)")}
				</Link>
				<Typography color="text.primary">{t("Promotion")} {promoId}</Typography>
			</Breadcrumbs>

			{/* Promotion Detail Card */}
			<Paper sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography variant="h5">
						{t("Promotion")} #{promoId}
					</Typography>
					{isSalesNewsAdmin && (
					<Link to={`/sales-news/sales-news-vgsap/promotion/edit/${promoId}`} style={{ textDecoration: 'none' }}>
						<Button variant="outlined" size="small" startIcon={<EditIcon />}>
							{t("Edit")}
						</Button>
					</Link>
				)}
				</Box>

				{/* Detail fields */}
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5, mt: 2 }}>
					<DetailField label={t("Promo ID")} value={promo.promo_id} />
					<DetailField label={t("Create Date")} value={formatDate(promo.create_dt)} />
					<DetailField label={t("Region")} value={promo.grp_id} />
					<DetailField label={t("Promo Start")} value={formatDate(promo.start_date)} />
					<DetailField label={t("Promo End")} value={formatDate(promo.end_date)} />
					<DetailField label={t("Bulletin Number")} value={promo.bulletin_no} />
					<DetailField label={t("CPG")} value={promo.scd_code} />
					<DetailField label={t("Created By")} value={promo.create_opid} />
					<DetailField label={t("Last Modified By")} value={promo.maint_opid ? `${promo.maint_opid} on ${formatDate(promo.maint_dt)}` : 'N/A'} />
				</Box>

				{/* Description */}
				<Box sx={{ mt: 2.5 }}>
					<DetailField label={t("Description")} value={promo.description} />
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Promotion Summary Points — grouped by section */}
				<Typography variant="h6" gutterBottom>{t("Summary Points")}</Typography>

				{Object.keys(POINT_SECTIONS).map((sectionId) => {
					const sectionPoints = pointsBySection[sectionId] || [];
					return (
						<Box key={sectionId} sx={{ mb: 2.5 }}>
							<Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
								{POINT_SECTIONS[sectionId]}
							</Typography>
							{sectionPoints.length > 0 ? (
								<Box sx={{ pl: 2, mt: 0.5 }}>
									{sectionPoints.map((point, idx) => (
										<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
											{point.points || point.Points || '—'}
										</Typography>
									))}
								</Box>
							) : (
								<Typography variant="body2" color="text.secondary" sx={{ pl: 2, mt: 0.5 }}>
									{t("No data")}
								</Typography>
							)}
						</Box>
					);
				})}

				<Divider sx={{ my: 3 }} />

				{/* Link to bulletin detail if bulletin_no available */}
				{promo.bulletin_no && promo.bulletin_no !== 'UNKNOWN' && (
					<Box>
						<Typography variant="subtitle2" color="text.secondary">{t("Related Sales News")}</Typography>
						{promo.bulletin_no.split(',').map((bn, i) => (
							<Link
								key={i}
								// fromPromo is passed both as router state (fast in-app nav) and as a
								// query param (survives a page refresh) so the bulletin breadcrumb
								// can always reconstruct the "Promotion {id}" trail.
								to={`/sales-news/sales-news-vgsap/detail/${bn.trim()}?fromPromo=${promoId}`}
								state={{ fromPromo: promoId }}
								style={{ color: "#1976d2", textDecoration: "none", marginRight: 8 }}
							>
								{bn.trim()}
							</Link>
						))}
					</Box>
				)}
			</Paper>
		</Box>
	);
}

export default PromotionDetail;
